import { lazy, Suspense, useState, useEffect, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Inline minimal spinner – no external deps in main chunk
function PageLoader() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

// Inline TooltipProvider wrapper to avoid extra lazy-load in the critical path
function SafeTooltipProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const [Provider, setProvider] = useState<React.ComponentType<{ children: ReactNode }> | null>(null);
  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    if (mounted) {
      import('@/components/ui/tooltip').then(m => setProvider(() => m.TooltipProvider));
    }
  }, [mounted]);
  if (!mounted || !Provider) return <>{children}</>;
  return <Provider>{children}</Provider>;
}

// Lazy load entire app content so route definitions, providers, and all page
// imports stay out of the main entry chunk – drastically cuts initial JS.
const AppContent = lazy(() =>
  import("./AppContent").catch(() => {
    // Stale chunk after deploy — reload once
    const key = "chunk_reload";
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      window.location.reload();
    }
    return import("./AppContent"); // let it throw naturally if reload didn't help
  })
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<PageLoader />}>
        <SafeTooltipProvider>
          <AppContent />
        </SafeTooltipProvider>
      </Suspense>
    </QueryClientProvider>
  );
};

export default App;
